import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui/src/lib/form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  AnalysesResultDetails,
  MeasurementConfiguration,
} from 'src/app/analyses/interfaces/analyses.types';
import { AnalysisId } from 'src/app/analyses/interfaces/analysis-id.enum';
import { AnalysesService } from 'src/app/analyses/services/analyses.service';
import { MeasurementAddHostDirective } from '../../directives/measurement-add-host.directive';
import { MeasurementAddBase } from '../../interfaces/measurement-add.interface';
import { MeasurementAnalysisResult } from '../../interfaces/measurement.interface';
import { MeasurementAddBloodPressureComponent } from '../forms/measurement-add-blood-pressure/measurement-add-blood-pressure.component';
import { MeasurementAddBmiComponent } from '../forms/measurement-add-bmi/measurement-add-bmi.component';
import { MeasurementAddBodyFatManualComponent } from '../forms/measurement-add-body-fat-manual/measurement-add-body-fat-manual.component';
import { MeasurementAddBodyFatComponent } from '../forms/measurement-add-body-fat/measurement-add-body-fat.component';
import { MeasurementAddWeightComponent } from '../forms/measurement-add-weight/measurement-add-weight.component';
import { MeasurementAddHeartRateComponent } from '../forms/measurement-add-heart-rate/measurement-add-heart-rate.component';
import { MeasurementAddHeightComponent } from '../forms/measurement-add-height/measurement-add-height.component';
import { MeasurementAddWaistCircumferenceComponent } from '../forms/measurement-add-waist-circumference/measurement-add-waist-circumference.component';
import { MeasurementAddHostService } from './measurement-add-host.service';
import { finalize, Observable } from 'rxjs';

enum ComponentState {
  LOADING = 'loading',
  ERROR = 'error',
  FORM = 'form',
}

@UntilDestroy()
@Component({
  selector: 'app-measurement-add-host',
  templateUrl: './measurement-add-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementAddHostComponent implements OnInit, OnChanges {
  @ViewChild(MeasurementAddHostDirective, { static: true })
  measurementAddHost!: MeasurementAddHostDirective;

  @Input() editMode = false;
  @Input() configuration: MeasurementConfiguration;
  @Input() analysisResult: MeasurementAnalysisResult;
  @Input() detailedResult: AnalysesResultDetails;
  @Input() formFieldAppearance = GpuFormFieldAppearance.Card;
  @Input() backVisible = true;

  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  protected state = ComponentState.LOADING;

  private componentRef: ComponentRef<MeasurementAddBase>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private analysesService: AnalysesService,
    private hostService: MeasurementAddHostService
  ) {}

  ngOnInit() {
    this.hostService.changeComponent$
      .pipe(untilDestroyed(this))
      .subscribe((selector: AnalysisId | string) =>
        this.loadComponent(selector)
      );
  }

  ngOnChanges(): void {
    // Result is only required if we're editing result
    if (
      this.configuration &&
      this.editMode === !!(this.analysisResult && this.detailedResult)
    ) {
      this.loadComponent(this.configuration.id as AnalysisId);
    }
  }

  private loadComponent(componentSelector: AnalysisId | string) {
    const component = this.selectComponent(componentSelector);
    this.state = ComponentState.FORM;

    const viewContainerRef = this.measurementAddHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent<MeasurementAddBase>(
      component
    );
    this.componentRef.instance.configuration = this.configuration;
    this.componentRef.instance.formFieldAppearance = this.formFieldAppearance;
    this.componentRef.instance.analysisResult = this.analysisResult;
    this.componentRef.instance.detailedResult = this.detailedResult;

    // Init is optional
    if (this.componentRef.instance.init) {
      this.componentRef.instance.init();
    }

    this.componentRef.instance.submitted
      .pipe(untilDestroyed(this))
      .subscribe(() => this.onSubmit());

    this.cdRef.markForCheck();
  }

  private onSubmit() {
    const data = this.componentRef.instance.onSubmit();
    this.state = ComponentState.LOADING;
    // Hide form while loading animation is visible
    this.componentRef.location.nativeElement.style.display = 'none';

    let method: Observable<{ requestId: string }>;
    if (!this.editMode) {
      method = this.analysesService.measurementCreate(data);
    } else {
      const id = this.analysisResult.result.requestId;
      method = this.analysesService.measurementUpdate(id, data);
    }

    method.pipe(finalize(() => (this.state = ComponentState.FORM))).subscribe({
      next: ({ requestId }) => this.saved.emit(requestId),
      error: (error) => {
        this.componentRef.instance.setError(error.error);
        this.componentRef.location.nativeElement.style.display = '';
      },
    });
  }

  private selectComponent(analysisId: AnalysisId | string) {
    return (
      {
        [AnalysisId.BodyWeight]: MeasurementAddWeightComponent,
        [AnalysisId.FatPercentageMale]: MeasurementAddBodyFatComponent,
        [AnalysisId.FatPercentageFemale]: MeasurementAddBodyFatComponent,
        [AnalysisId.BodyMassIndexMale]: MeasurementAddBmiComponent,
        [AnalysisId.BodyMassIndexFemale]: MeasurementAddBmiComponent,
        [AnalysisId.WaistCircumference]: MeasurementAddWaistCircumferenceComponent,
        [AnalysisId.BloodPressure]: MeasurementAddBloodPressureComponent,
        [AnalysisId.HeartRate]: MeasurementAddHeartRateComponent,
        [AnalysisId.BodyHeight]: MeasurementAddHeightComponent,
        bfpManual: MeasurementAddBodyFatManualComponent,
      }[analysisId] ?? null
    );
  }
}
